import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Activity,
  Edit3,
  CheckCircle,
  Copy,
  Database,
  Download,
  ExternalLink,
  FileArchive,
  Globe,
  Lock,
  Presentation,
  RefreshCw,
  Server,
  ShieldCheck,
  ChevronDown,
  ChevronRight,
  Plus,
  Upload,
  Trash2,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/components/ui/table';
import { presentationHubAPI } from '@/shared/services/api';
import { getPresentationEditorPath, PRESENTATION_HUB_PUBLIC_ROUTES } from '@/modules/crm/presentation-hub/presentationHubRoutes';

const SERVICE_CHECKS = [
  {
    label: 'Local-first implementation',
    value: 'Locked',
    detail: 'Finish and verify the EzERP local implementation before creating any server deployment plan.'
  },
  {
    label: 'Absorbed frontend/backend',
    value: 'Required',
    detail: 'Presentation Hub UI and APIs must become native EzERP CRM/frontend/backend features.'
  },
  {
    label: 'Public no-login slugs',
    value: 'Required',
    detail: 'Published presentation links must open without credentials while admin tools remain protected.'
  },
  {
    label: 'Server deployment',
    value: 'Deferred',
    detail: 'No NGINX alias or standalone service work until the local EzERP-native version is approved.'
  }
];

const getPublicUrl = (path) => {
  if (typeof window === 'undefined') return path;
  return `${window.location.origin}${path}`;
};

const getPortalPublicUrl = (slug) => {
  if (typeof window === 'undefined') return `/portal/${slug}`;
  return `${window.location.origin}/portal/${slug}`;
};

const formatActivityDate = (value) => {
  if (!value) return 'Unknown';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const escapeCsvValue = (value) => {
  const text = value === undefined || value === null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const downloadTextFile = (filename, content, type) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

function StatusBadge({ status }) {
  if (status === 'ready') {
    return <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">Ready</Badge>;
  }

  if (status === 'candidate') {
    return <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">Candidate</Badge>;
  }

  return <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">Planned</Badge>;
}

function CheckBadge({ check }) {
  if (!check) {
    return <Badge variant="outline" className="border-slate-200 bg-slate-50 text-slate-600">Unknown</Badge>;
  }

  if (check.ok) {
    return <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">OK</Badge>;
  }

  return <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">Needs attention</Badge>;
}

function ActivityBadge({ action, result }) {
  const failed = result && result !== 'success';
  const labels = {
    portal_view: 'Portal View',
    deck_open: 'Deck Open',
    portal_not_found: 'Not Found',
  };

  if (failed || action === 'portal_not_found') {
    return <Badge variant="outline" className="border-red-200 bg-red-50 text-red-700">{labels[action] || action}</Badge>;
  }

  if (action === 'deck_open') {
    return <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">Deck Open</Badge>;
  }

  return <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700">{labels[action] || action || 'Activity'}</Badge>;
}

function CopyButton({ value, label = "Copy" }) {
  const handleCopy = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = value;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <Button variant="outline" size="sm" className="gap-2" onClick={handleCopy}>
      <Copy className="h-4 w-4" />
      {label}
    </Button>
  );
}

export default function PresentationHub() {
  const [hubStatus, setHubStatus] = useState(null);
  const [statusError, setStatusError] = useState('');
  
  // Section Collapse/Expand State
  const [showDecksBlock, setShowDecksBlock] = useState(true);
  const [showPortalsBlock, setShowPortalsBlock] = useState(true);

  // Portals CRUD State
  const [portals, setPortals] = useState([]);
  const [portalsLoading, setPortalsLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPortal, setEditingPortal] = useState(null);
  const [presentationModalOpen, setPresentationModalOpen] = useState(false);
  const [editingPresentation, setEditingPresentation] = useState(null);
  const [presentationForm, setPresentationForm] = useState({
    name: '',
    audience: '',
    slug: '',
    languages: 'en',
    status: 'ready',
    html: ''
  });
  const [presentationSaving, setPresentationSaving] = useState(false);
  const [activityModalOpen, setActivityModalOpen] = useState(false);
  const [activityPortal, setActivityPortal] = useState(null);
  const [activityRecords, setActivityRecords] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState('');
  const [activitySearch, setActivitySearch] = useState('');
  const [activityActionFilter, setActivityActionFilter] = useState('all');

  // Form Fields State
  const [recipient, setRecipient] = useState('');
  const [slug, setSlug] = useState('');
  const [autoSuffix, setAutoSuffix] = useState(true);
  const [selectedDecks, setSelectedDecks] = useState([]);

  // Load Status and Portals
  const fetchHubData = () => {
    presentationHubAPI.getStatus(false)
      .then((data) => {
        setHubStatus(data);
        setStatusError('');
      })
      .catch((error) => {
        setStatusError(error.message || 'Presentation Hub status unavailable');
      });

    setPortalsLoading(true);
    presentationHubAPI.getPortals()
      .then((data) => {
        setPortals(data);
      })
      .catch((error) => {
        console.error('Error fetching portals:', error);
      })
      .finally(() => {
        setPortalsLoading(false);
      });
  };

  useEffect(() => {
    fetchHubData();
  }, []);

  const aliases = hubStatus?.presentations?.length ? hubStatus.presentations : PRESENTATION_HUB_PUBLIC_ROUTES;
  const localFirstCheck = hubStatus?.checks?.find((item) => item.id === 'local-first');
  const absorbedBackendCheck = hubStatus?.checks?.find((item) => item.id === 'absorbed-backend');
  const deckLookup = useMemo(() => {
    const map = new Map();
    [...PRESENTATION_HUB_PUBLIC_ROUTES, ...aliases].forEach((item) => {
      if (item?.id) {
        map.set(item.id, item);
      }
    });
    return map;
  }, [aliases]);
  const publicRows = useMemo(() => aliases.map((item) => ({
    ...item,
    url: getPublicUrl(item.publicPath)
  })), [aliases]);
  const filteredActivityRecords = useMemo(() => {
    const query = activitySearch.trim().toLowerCase();
    return activityRecords.filter((record) => {
      const actionMatch = activityActionFilter === 'all' || record.action === activityActionFilter;
      const haystack = [
        record.portalSlug,
        record.recipient,
        record.action,
        record.deckName,
        record.deckId,
        record.ipAddress,
        record.result,
        record.userAgent,
        record.referer,
        record.evidenceHash,
      ].filter(Boolean).join(' ').toLowerCase();
      return actionMatch && (!query || haystack.includes(query));
    });
  }, [activityRecords, activitySearch, activityActionFilter]);
  const activitySummary = useMemo(() => {
    const uniqueIps = new Set(activityRecords.map((record) => record.ipAddress).filter(Boolean));
    return {
      total: activityRecords.length,
      views: activityRecords.filter((record) => record.action === 'portal_view').length,
      deckOpens: activityRecords.filter((record) => record.action === 'deck_open').length,
      failures: activityRecords.filter((record) => record.result && record.result !== 'success').length,
      uniqueIps: uniqueIps.size,
    };
  }, [activityRecords]);

  const resetPresentationForm = (presentation = null) => {
    setEditingPresentation(presentation);
    setPresentationForm({
      name: presentation?.name || '',
      audience: presentation?.audience || '',
      slug: presentation?.publicPath?.startsWith('/presentation/')
        ? presentation.publicPath.replace('/presentation/', '')
        : '',
      languages: (presentation?.languages || ['en']).join(', '),
      status: presentation?.status || 'ready',
      html: presentation?.html || ''
    });
    setPresentationModalOpen(true);
  };

  // Open Modal for Create
  const handleOpenCreate = () => {
    setEditingPortal(null);
    setRecipient('');
    setSlug('');
    setAutoSuffix(true);
    setSelectedDecks([]);
    setModalOpen(true);
  };

  const handleOpenCreateForPresentation = (presentation) => {
    setEditingPortal(null);
    setRecipient('');
    setSlug('');
    setAutoSuffix(true);
    setSelectedDecks([presentation.id]);
    setShowPortalsBlock(true);
    setModalOpen(true);
  };

  // Open Modal for Edit
  const handleOpenEdit = (portal) => {
    setEditingPortal(portal);
    setRecipient(portal.recipient);
    setSlug(portal.slug);
    setAutoSuffix(false);
    setSelectedDecks(portal.decks || []);
    setModalOpen(true);
  };

  // Autogenerate slug suggestion
  useEffect(() => {
    if (!editingPortal && autoSuffix && recipient) {
      const baseSlug = recipient
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)+/g, '');
      const randomSuffix = Math.random().toString(36).substring(2, 7);
      setSlug(`${baseSlug}-${randomSuffix}`);
    }
  }, [recipient, autoSuffix, editingPortal]);

  // Submit Portal Form
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!recipient.trim() || !slug.trim()) {
      toast.error('Recipient and Slug are required.');
      return;
    }

    const payload = {
      recipient: recipient.trim(),
      slug: slug.trim(),
      decks: selectedDecks
    };

    try {
      if (editingPortal) {
        await presentationHubAPI.updatePortal(editingPortal.id, payload);
        toast.success('Portal updated successfully.');
      } else {
        await presentationHubAPI.createPortal(payload);
        toast.success('Portal link created successfully.');
      }
      setModalOpen(false);
      fetchHubData();
    } catch (err) {
      toast.error(err.message || 'Failed to save presentation portal.');
    }
  };

  const handlePresentationNameChange = (value) => {
    setPresentationForm((current) => {
      const next = { ...current, name: value };
      if (!editingPresentation && !current.slug.trim()) {
        next.slug = value
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)+/g, '');
      }
      return next;
    });
  };

  const handlePresentationFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.html') && !file.name.toLowerCase().endsWith('.htm')) {
      toast.error('Please select an HTML file.');
      return;
    }
    const html = await file.text();
    setPresentationForm((current) => {
      const fallbackName = file.name.replace(/\.(html|htm)$/i, '').trim();
      const nextName = current.name || fallbackName;
      return {
        ...current,
        name: nextName,
        slug: current.slug || nextName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, ''),
        html
      };
    });
    toast.success('HTML loaded into the presentation draft.');
  };

  const handlePresentationSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      name: presentationForm.name.trim(),
      audience: presentationForm.audience.trim(),
      slug: presentationForm.slug.trim(),
      languages: presentationForm.languages
        .split(',')
        .map((item) => item.trim().toLowerCase())
        .filter(Boolean),
      status: presentationForm.status,
      html: presentationForm.html
    };

    if (!payload.name || !payload.slug || !payload.html) {
      toast.error('Name, slug, and HTML content are required.');
      return;
    }

    setPresentationSaving(true);
    try {
      if (editingPresentation) {
        await presentationHubAPI.updatePresentation(editingPresentation.id, payload);
        toast.success('Presentation updated.');
      } else {
        await presentationHubAPI.createPresentation(payload);
        toast.success('Presentation added.');
      }
      setPresentationModalOpen(false);
      setEditingPresentation(null);
      fetchHubData();
    } catch (err) {
      toast.error(err.message || 'Failed to save presentation.');
    } finally {
      setPresentationSaving(false);
    }
  };

  const fetchActivity = async (portal = activityPortal) => {
    setActivityLoading(true);
    setActivityError('');
    try {
      const filters = { limit: portal ? 500 : 1000 };
      const data = portal
        ? await presentationHubAPI.getPortalActivity(portal.slug, filters)
        : await presentationHubAPI.getAllPortalActivity(filters);
      setActivityRecords(Array.isArray(data) ? data : []);
    } catch (err) {
      setActivityError(err.message || 'Failed to load portal activity.');
      setActivityRecords([]);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleOpenActivity = (portal = null) => {
    setActivityPortal(portal);
    setActivitySearch('');
    setActivityActionFilter('all');
    setActivityModalOpen(true);
    fetchActivity(portal);
  };

  const handleExportActivity = (format) => {
    if (!filteredActivityRecords.length) {
      toast.error('No activity records to export.');
      return;
    }
    const scope = activityPortal?.slug || 'all-portals';
    if (format === 'json') {
      downloadTextFile(
        `presentation-hub-activity-${scope}.json`,
        JSON.stringify(filteredActivityRecords, null, 2),
        'application/json'
      );
      return;
    }
    const headers = [
      'createdAt',
      'action',
      'result',
      'recipient',
      'portalSlug',
      'deckName',
      'deckId',
      'ipAddress',
      'userAgent',
      'referer',
      'evidenceHash',
    ];
    const lines = [
      headers.join(','),
      ...filteredActivityRecords.map((record) => headers.map((header) => escapeCsvValue(record[header])).join(',')),
    ];
    downloadTextFile(`presentation-hub-activity-${scope}.csv`, lines.join('\n'), 'text/csv');
  };

  // Delete Portal
  const handleDeletePortal = async (portalId) => {
    if (!window.confirm('Are you sure you want to delete this custom client portal link?')) {
      return;
    }
    try {
      await presentationHubAPI.deletePortal(portalId);
      toast.success('Portal deleted.');
      fetchHubData();
    } catch (err) {
      toast.error(err.message || 'Failed to delete portal.');
    }
  };

  // Toggle Deck selection
  const handleToggleDeck = (deckId) => {
    if (selectedDecks.includes(deckId)) {
      setSelectedDecks(selectedDecks.filter(id => id !== deckId));
    } else {
      setSelectedDecks([...selectedDecks, deckId]);
    }
  };

  return (
    <div className="space-y-6" data-testid="crm-presentation-hub">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">The Presentation HUB</h1>
          <p className="mt-1 text-muted-foreground">
            Native CRM control surface for public EzERP, partner, Emissor, and campaign presentations.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="gap-2" asChild>
            <Link to="/crm/marketing-hub">
              <FileArchive className="h-4 w-4" />
              Sales Assets
            </Link>
          </Button>
          <Button variant="outline" className="gap-2" onClick={fetchHubData}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card className="border border-border shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-50">
                <Presentation className="h-5 w-5 text-teal-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Public Slugs</p>
                <p className="text-2xl font-bold">{aliases.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50">
                <Server className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Runtime Model</p>
                <p className="text-lg font-bold">EzERP Native</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-50">
                <Database className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Local First</p>
                <div className="mt-1"><CheckBadge check={localFirstCheck} /></div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50">
                <Lock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">EzERP API</p>
                <div className="mt-1"><CheckBadge check={absorbedBackendCheck} /></div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {(hubStatus || statusError) && (
        <Card className={statusError ? 'border-amber-200 bg-amber-50' : 'border-green-200 bg-green-50'}>
          <CardContent className="p-4">
            {statusError ? (
              <div className="flex items-start gap-3 text-amber-800">
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-none" />
                <div>
                  <p className="font-medium">Presentation Hub status is not reachable from EzERP yet.</p>
                  <p className="mt-1 text-sm">{statusError}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3 text-green-800">
                <CheckCircle className="mt-0.5 h-5 w-5 flex-none" />
                <div>
                  <p className="font-medium">Presentation Hub architecture is locked to local-first EzERP-native integration.</p>
                  <p className="mt-1 text-sm">
                    Admin route: {hubStatus.architecture.adminRoute}; public access: {hubStatus.architecture.publicAccess}.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* BLOCK 1: Public Presentations (Collapsible) */}
      <Card className="border border-border shadow-sm">
        <CardHeader className="cursor-pointer select-none pb-3" onClick={() => setShowDecksBlock(!showDecksBlock)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-teal-600" />
              <CardTitle className="text-xl">Public Presentations</CardTitle>
            </div>
            <div className="flex items-center gap-3">
              {showDecksBlock && (
                <Button
                  size="sm"
                  className="gap-2 bg-teal-600 text-white hover:bg-teal-700"
                  onClick={(event) => {
                    event.stopPropagation();
                    resetPresentationForm();
                  }}
                >
                  <Plus className="h-4 w-4" />
                  Adicionar Nova Apresentação
                </Button>
              )}
              {showDecksBlock ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
            </div>
          </div>
          {showDecksBlock && (
            <CardDescription className="mt-1">
              These are the customer-facing URLs EzERP exposes locally first, then later in production after local approval.
            </CardDescription>
          )}
        </CardHeader>
        {showDecksBlock && (
          <CardContent className="pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Presentation</TableHead>
                  <TableHead>Public URL</TableHead>
                  <TableHead>Source Route</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {publicRows.map((item) => (
                  <TableRow key={item.publicPath}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{item.name}</p>
                        <p className="text-xs text-muted-foreground">{item.audience}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{item.publicPath}</TableCell>
                    <TableCell className="font-mono text-sm">{item.sourceRoute}</TableCell>
                    <TableCell><StatusBadge status={item.status} /></TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800"
                          onClick={() => handleOpenCreateForPresentation(item)}
                          title="Create a tracked portal link for this presentation"
                        >
                          <Presentation className="h-4 w-4" />
                          Portal
                        </Button>
                        <CopyButton value={item.url} />
                        {item.custom ? (
                          <Button variant="outline" size="sm" className="gap-2" onClick={() => resetPresentationForm(item)}>
                            <Edit3 className="h-4 w-4" />
                            Edit
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" className="gap-2" asChild>
                            <Link to={getPresentationEditorPath(item.sourceDeck)}>
                              <Edit3 className="h-4 w-4" />
                              Edit
                            </Link>
                          </Button>
                        )}
                        {['ready', 'contract'].includes(item.status) ? (
                          <Button variant="outline" size="sm" className="gap-2" asChild>
                            <a href={item.publicPath} target="_blank" rel="noreferrer">
                              <ExternalLink className="h-4 w-4" />
                              Open
                            </a>
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" className="gap-2" disabled>
                            <ExternalLink className="h-4 w-4" />
                            Open
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        )}
      </Card>

      {/* BLOCK 2: Portals for Presentations (Collapsible & CRUD) */}
      <Card className="border border-border shadow-sm">
        <CardHeader className="cursor-pointer select-none pb-3" onClick={() => setShowPortalsBlock(!showPortalsBlock)}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Presentation className="h-5 w-5 text-indigo-600" />
              <CardTitle className="text-xl">Portals for Presentations</CardTitle>
            </div>
            <div className="flex items-center gap-4">
              {showPortalsBlock && (
                <>
                  <Button 
                    variant="outline"
                    size="sm" 
                    className="gap-1 border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenActivity();
                    }}
                  >
                    <Activity className="h-4 w-4" />
                    All Activity
                  </Button>
                  <Button 
                    size="sm" 
                    className="gap-1 bg-indigo-600 text-white hover:bg-indigo-700" 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenCreate();
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Create Portal
                  </Button>
                </>
              )}
              {showPortalsBlock ? <ChevronDown className="h-5 w-5 text-muted-foreground" /> : <ChevronRight className="h-5 w-5 text-muted-foreground" />}
            </div>
          </div>
          {showPortalsBlock && (
            <CardDescription className="mt-1">
              Configure tailored portals with selected presentations for specific clients. Safe, isolated, and accessible without credentials.
            </CardDescription>
          )}
        </CardHeader>
        {showPortalsBlock && (
          <CardContent className="pt-0">
            {portalsLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground animate-pulse">
                Loading client portals...
              </div>
            ) : portals.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                <p className="mb-2">No client portals generated yet.</p>
                <Button variant="outline" size="sm" className="gap-1" onClick={handleOpenCreate}>
                  <Plus className="h-4 w-4" />
                  Generate First Portal
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Custom Slug</TableHead>
                    <TableHead>Assigned Decks</TableHead>
                    <TableHead>Generated Link</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {portals.map((portal) => (
                    <TableRow key={portal.id}>
                      <TableCell className="font-medium">{portal.recipient}</TableCell>
                      <TableCell className="font-mono text-sm">{portal.slug}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(portal.decks || []).map((deckId) => {
                            const found = deckLookup.get(deckId);
                            return (
                              <Badge key={deckId} variant="secondary" className="text-xs">
                                {found ? found.name : deckId}
                              </Badge>
                            );
                          })}
                          {(!portal.decks || portal.decks.length === 0) && (
                            <span className="text-xs text-muted-foreground">None</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs max-w-[240px] truncate text-muted-foreground">
                        {getPortalPublicUrl(portal.slug)}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1 border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:text-indigo-800"
                            onClick={() => handleOpenActivity(portal)}
                          >
                            <Activity className="h-3.5 w-3.5" />
                            Activity
                          </Button>
                          <CopyButton value={getPortalPublicUrl(portal.slug)} label="Link" />
                          <Button variant="outline" size="sm" className="gap-1" onClick={() => handleOpenEdit(portal)}>
                            <Edit3 className="h-3.5 w-3.5" />
                            Edit
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={() => handleDeletePortal(portal.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Delete
                          </Button>
                          <Button variant="outline" size="sm" className="gap-1" asChild>
                            <a href={`/portal/${portal.slug}`} target="_blank" rel="noreferrer">
                              <ExternalLink className="h-3.5 w-3.5" />
                              Open
                            </a>
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="h-5 w-5 text-blue-600" />
              Architecture Lock Checks
            </CardTitle>
            <CardDescription>
              These checks come from EzERP backend and keep the corrected local-first architecture visible.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Check</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Target</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(hubStatus?.checks || []).map((check) => (
                  <TableRow key={check.id}>
                    <TableCell>
                      <p className="font-medium">{check.label}</p>
                      {check.reason ? <p className="text-xs text-muted-foreground">{check.reason}</p> : null}
                    </TableCell>
                    <TableCell><CheckBadge check={check} /></TableCell>
                    <TableCell className="max-w-[300px] text-xs text-muted-foreground">{check.detail}</TableCell>
                  </TableRow>
                ))}
                {!hubStatus?.checks?.length && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-sm text-muted-foreground">
                      Architecture checks will appear after the EzERP backend exposes `/api/presentation-hub/status`.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-blue-600" />
              Integration Guardrails
            </CardTitle>
            <CardDescription>
              The hub must stay inside EzERP locally until its routes, data, and admin surface are proven.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {SERVICE_CHECKS.map((item) => (
              <div key={item.label} className="rounded-lg border p-3">
                <div className="flex items-center justify-between gap-3">
                  <p className="font-medium">{item.label}</p>
                  <Badge variant="outline" className="border-blue-200 bg-blue-50 text-blue-700">{item.value}</Badge>
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{item.detail}</p>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              Before Production
            </CardTitle>
            <CardDescription>
              These items must be checked before any server deployment plan exists.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {[
              'Convert the packaged Presentation Hub UI into native EzERP React components.',
              'Move Presentation Hub data/API responsibilities into EzERP backend.',
              'Keep individual public presentation links no-login.',
              'Keep /crm/presentation-hub protected by EzERP authentication.',
              'Confirm Brazilian Rewards route before enabling its public slug.'
            ].map((item) => (
              <div key={item} className="flex items-start gap-2 rounded-lg border p-3">
                <CheckCircle className="mt-0.5 h-4 w-4 flex-none text-green-600" />
                <span>{item}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {activityModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-6xl rounded-lg border border-border bg-background p-6 shadow-lg space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b pb-3">
              <div>
                <h2 className="flex items-center gap-2 text-xl font-semibold font-['Space_Grotesk']">
                  <Activity className="h-5 w-5 text-indigo-600" />
                  {activityPortal ? 'Portal Activity' : 'All Portal Activity'}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {activityPortal
                    ? `${activityPortal.recipient} - /portal/${activityPortal.slug}`
                    : 'Every recorded portal view, deck open, and invalid portal attempt.'}
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setActivityModalOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="grid gap-3 md:grid-cols-5">
              {[
                ['Total Events', activitySummary.total],
                ['Portal Views', activitySummary.views],
                ['Deck Opens', activitySummary.deckOpens],
                ['Unique IPs', activitySummary.uniqueIps],
                ['Failures', activitySummary.failures],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border p-3">
                  <p className="text-xs text-muted-foreground">{label}</p>
                  <p className="text-2xl font-bold">{value}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3 rounded-lg border p-3 md:flex-row md:items-center md:justify-between">
              <div className="flex flex-1 flex-col gap-2 md:flex-row">
                <input
                  type="text"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring md:max-w-sm"
                  value={activitySearch}
                  onChange={(event) => setActivitySearch(event.target.value)}
                  placeholder="Search recipient, portal, IP, browser, evidence..."
                />
                <select
                  className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={activityActionFilter}
                  onChange={(event) => setActivityActionFilter(event.target.value)}
                >
                  <option value="all">All actions</option>
                  <option value="portal_view">Portal views</option>
                  <option value="deck_open">Deck opens</option>
                  <option value="portal_not_found">Not found</option>
                </select>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" className="gap-2" onClick={() => fetchActivity(activityPortal)} disabled={activityLoading}>
                  <RefreshCw className={`h-4 w-4 ${activityLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                <Button variant="outline" size="sm" className="gap-2" onClick={() => handleExportActivity('csv')}>
                  <Download className="h-4 w-4" />
                  CSV
                </Button>
                <Button variant="outline" size="sm" className="gap-2" onClick={() => handleExportActivity('json')}>
                  <Download className="h-4 w-4" />
                  JSON
                </Button>
              </div>
            </div>

            {activityError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {activityError}
              </div>
            )}

            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Recipient</TableHead>
                    <TableHead>Portal</TableHead>
                    <TableHead>Presentation</TableHead>
                    <TableHead>IP</TableHead>
                    <TableHead>Evidence</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {activityLoading ? (
                    <TableRow>
                      <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                        Loading activity...
                      </TableCell>
                    </TableRow>
                  ) : filteredActivityRecords.length ? (
                    filteredActivityRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          {formatActivityDate(record.createdAt)}
                        </TableCell>
                        <TableCell><ActivityBadge action={record.action} result={record.result} /></TableCell>
                        <TableCell className="font-medium">{record.recipient || 'Unknown'}</TableCell>
                        <TableCell className="font-mono text-xs">{record.portalLink || `/portal/${record.portalSlug}`}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{record.deckName || record.deckId || 'Portal landing'}</p>
                            {record.deckPublicPath ? (
                              <p className="font-mono text-xs text-muted-foreground">{record.deckPublicPath}</p>
                            ) : null}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{record.ipAddress || 'Unknown'}</TableCell>
                        <TableCell>
                          <div className="max-w-[260px] space-y-1">
                            <p className="truncate font-mono text-xs">{record.evidenceHash}</p>
                            <p className="truncate text-xs text-muted-foreground">{record.userAgent || 'No user agent'}</p>
                            {record.referer ? (
                              <p className="truncate text-xs text-muted-foreground">Ref: {record.referer}</p>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} className="py-8 text-center text-sm text-muted-foreground">
                        No matching portal activity records yet.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      )}

      {presentationModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-3xl rounded-lg border border-border bg-background p-6 shadow-lg space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b pb-2">
              <div>
                <h2 className="text-xl font-semibold font-['Space_Grotesk']">
                  {editingPresentation ? 'Editar Apresentação' : 'Adicionar Nova Apresentação'}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Import an HTML presentation and publish it as a no-login EzERP public route.
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setPresentationModalOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <form onSubmit={handlePresentationSubmit} className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Presentation Name</label>
                  <input
                    type="text"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    value={presentationForm.name}
                    onChange={(event) => handlePresentationNameChange(event.target.value)}
                    placeholder="EzERP - Built for Control. Powered for Growth"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Public Slug</label>
                  <input
                    type="text"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                    value={presentationForm.slug}
                    onChange={(event) => setPresentationForm((current) => ({
                      ...current,
                      slug: event.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-{2,}/g, '-')
                    }))}
                    required
                  />
                  <p className="text-xs text-muted-foreground font-mono">
                    Final public path: /presentation/{presentationForm.slug || '...'}
                  </p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-medium">Audience / Description</label>
                  <input
                    type="text"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    value={presentationForm.audience}
                    onChange={(event) => setPresentationForm((current) => ({ ...current, audience: event.target.value }))}
                    placeholder="Prospects, clients, and enterprise buyers"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Languages</label>
                  <input
                    type="text"
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                    value={presentationForm.languages}
                    onChange={(event) => setPresentationForm((current) => ({ ...current, languages: event.target.value }))}
                    placeholder="en, pt, es"
                  />
                </div>
              </div>

              <div className="space-y-2 rounded-lg border p-3">
                <label className="text-sm font-medium">HTML File</label>
                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-teal-300 bg-teal-50 px-3 py-4 text-sm font-medium text-teal-800 hover:bg-teal-100">
                  <Upload className="h-4 w-4" />
                  Import HTML presentation
                  <input type="file" accept=".html,.htm,text/html" className="hidden" onChange={handlePresentationFile} />
                </label>
                <p className="text-xs text-muted-foreground">
                  HTML loaded: {presentationForm.html ? `${presentationForm.html.length.toLocaleString()} characters` : 'none yet'}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">HTML Content</label>
                <textarea
                  className="min-h-44 w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                  value={presentationForm.html}
                  onChange={(event) => setPresentationForm((current) => ({ ...current, html: event.target.value }))}
                  placeholder="Import an HTML file or paste HTML here."
                  required
                />
              </div>

              <div className="flex justify-end gap-2 border-t pt-3">
                <Button type="button" variant="outline" onClick={() => setPresentationModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-teal-600 text-white hover:bg-teal-700" disabled={presentationSaving}>
                  {presentationSaving ? 'Saving...' : editingPresentation ? 'Save Presentation' : 'Add Presentation'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CRUD Form Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-lg rounded-lg border border-border bg-background p-6 shadow-lg space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b pb-2">
              <h2 className="text-xl font-semibold font-['Space_Grotesk']">
                {editingPortal ? 'Edit Client Portal' : 'Generate Presentation Portal'}
              </h2>
              <Button variant="ghost" size="icon" onClick={() => setModalOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Recipient Name</label>
                <input
                  type="text"
                  placeholder="e.g., Tarik, Leo"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  value={recipient}
                  onChange={(e) => setRecipient(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Link Slug</label>
                  {!editingPortal && (
                    <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoSuffix}
                        onChange={(e) => setAutoSuffix(e.target.checked)}
                      />
                      Append secure suffix
                    </label>
                  )}
                </div>
                <input
                  type="text"
                  placeholder="e.g., tarik-7d2a9"
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-_]/g, ''))}
                  required
                />
                <p className="text-xs text-muted-foreground font-mono">
                  Final link path: /portal/{slug || '...'}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium block">Select Assigned Presentations</label>
                <div className="border rounded-md p-3 bg-muted/40 max-h-48 overflow-y-auto space-y-2">
                  {aliases.map((deck) => (
                    <label key={deck.id} className="flex items-start gap-2.5 text-sm cursor-pointer hover:bg-muted/60 p-1 rounded">
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={selectedDecks.includes(deck.id)}
                        onChange={() => handleToggleDeck(deck.id)}
                      />
                      <div>
                        <span className="font-medium text-foreground">{deck.name}</span>
                        <span className="block text-xs text-muted-foreground">{deck.audience}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t pt-3">
                <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-indigo-600 text-white hover:bg-indigo-700">
                  {editingPortal ? 'Save Changes' : 'Generate Link'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
